import { useCallback, useMemo, useState } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import {
  AvailablePaymentCurrency,
  DonationFormCategory,
  FormType,
  PaymentMethod,
  StripeRecurringInterval,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSnackbar } from "notistack";

import { isDeclineSolvedByContactingBank, PaymentErrorReport, unknownPaymentErrorCode } from "./helper";
import type { PaymentErrorContextType } from "./PaymentErrorContext";

import type { StripePaymentOrganization } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/StripePaymentComponent/types";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { PaymentMode } from "@/common/helpers/stripe";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { AmplitudeContext } from "@/components/PaymentProcessor/type";

export type UsePaymentErrorHandlerParams = {
  onPaymentError?: (report: PaymentErrorReport) => void;
  interceptError?: (report: PaymentErrorReport) => boolean;
  formId: string;
  commandId: string | null;
  organization: StripePaymentOrganization | null | undefined;
  setIsSubmitting: (value: boolean) => void;
  setIsPadLoading: (value: boolean) => void;
  formAmplitudeContext: AmplitudeContext;
  formType: FormType;
  tip: number;
  selectedPaymentMethod: PaymentMethod | undefined;
  isEmbed: boolean;
  stripeRecurrenceInterval: StripeRecurringInterval | null | undefined;
  currency: AvailablePaymentCurrency | undefined;
  category: TicketingFormCategory | DonationFormCategory;
  prioritizeBankPayment: boolean;
  expressCheckoutDisabled: boolean;
  paymentMode: PaymentMode;
};

/**
 * Central payment error policy: Datadog, Amplitude, inline banner, clear loading, optional
 * `onPaymentError` (e.g. form-only `reOpenCommand` — not called from standalone / invoice).
 * Pass the returned value as `paymentErrorState` on the Form V2 path to avoid a nested
 * `PaymentErrorContext` consumer under Stripe `Elements`.
 */
export function usePaymentErrorHandler({
  onPaymentError,
  interceptError,
  formId,
  commandId,
  organization,
  setIsSubmitting,
  setIsPadLoading,
  formAmplitudeContext,
  formType,
  tip,
  selectedPaymentMethod,
  isEmbed,
  stripeRecurrenceInterval,
  currency,
  category,
  prioritizeBankPayment,
  expressCheckoutDisabled,
  paymentMode,
}: UsePaymentErrorHandlerParams): PaymentErrorContextType {
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isBankConnectionGuidanceVisible, setIsBankConnectionGuidanceVisible] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const { t } = useTranslate();

  const clearPaymentError = useCallback(() => {
    setPaymentError(null);
    setIsBankConnectionGuidanceVisible(false);
  }, []);

  const showBankConnectionGuidance = useCallback(() => {
    setPaymentError(null);
    setIsBankConnectionGuidanceVisible(true);
  }, []);

  const handlePaymentError = useCallback(
    (errorReport: PaymentErrorReport) => {
      if (interceptError?.(errorReport)) {
        setIsSubmitting(false);
        setIsPadLoading(false);
        return;
      }

      const currentFormData = {
        formId,
        organizationId: organization?.id,
        country: organization?.country,
        paymentMethod: selectedPaymentMethod,
        commandId,
        expressCheckoutDisabled,
        stripeRecurrenceInterval,
        tip,
        isEmbed,
        formType,
        formCategory: category,
        currency,
        category,
        prioritizeBankPayment,
        paymentMode,
      };

      datadogLogs.logger.error(AmplitudeEvents.FormClickSubmitPaymentError, {
        ...currentFormData,
        ...errorReport,
      });
      logAmplitudeEvent(AmplitudeEvents.FormClickSubmitPaymentError, {
        ...formAmplitudeContext,
        ...currentFormData,
        ...errorReport,
      });

      const isPaymentSucceeded = errorReport.isPaymentSucceeded ?? false;

      if (isPaymentSucceeded) {
        enqueueSnackbar(t("common", "paymentSucceededWithErrorsMessage") + " " + errorReport.rawErrorMessage, {
          vibe: "positive",
          persist: true,
        });
      } else {
        setIsBankConnectionGuidanceVisible(false);
        setPaymentError(getPaymentErrorMessage(errorReport, t));
      }

      setIsSubmitting(false);
      setIsPadLoading(false);
      onPaymentError?.(errorReport);
    },
    [
      formId,
      organization?.id,
      organization?.country,
      selectedPaymentMethod,
      commandId,
      expressCheckoutDisabled,
      stripeRecurrenceInterval,
      tip,
      isEmbed,
      formType,
      category,
      currency,
      prioritizeBankPayment,
      paymentMode,
      logAmplitudeEvent,
      formAmplitudeContext,
      enqueueSnackbar,
      t,
      setIsSubmitting,
      setIsPadLoading,
      onPaymentError,
      interceptError,
    ]
  );

  return useMemo(
    () => ({
      paymentError,
      handlePaymentError,
      clearPaymentError,
      isBankConnectionGuidanceVisible,
      showBankConnectionGuidance,
    }),
    [paymentError, handlePaymentError, clearPaymentError, isBankConnectionGuidanceVisible, showBankConnectionGuidance]
  );
}

const BANK_ACCOUNT_NOT_CONNECTED_ERROR_CODE = "instant_verification_incomplete";

const getPaymentErrorMessage = (errorReport: PaymentErrorReport, t: ReturnType<typeof useTranslate>["t"]): string => {
  if (
    errorReport.rawErrorCode === "stripe_initialization_failed" ||
    errorReport.rawErrorCode === "stripe_payment_element_load_error"
  ) {
    return t("common", "unableToLoadStripe");
  }
  if (errorReport.rawErrorCode === BANK_ACCOUNT_NOT_CONNECTED_ERROR_CODE) {
    return t("common", "payment.bank.notConnected");
  }
  if (errorReport.rawErrorCode === "sepa_payment_capability_blocked") {
    return t("common", "payment.sepa.unavailable");
  }
  if (isDeclineSolvedByContactingBank(errorReport.declineCode)) {
    return t("common", "payment.declined.contactBank");
  }
  return errorReport.rawErrorMessage || t("common", "unknown_error", { code: unknownPaymentErrorCode.other1.code });
};
